import type { GatewayBrowserClient } from "../gateway.ts";
import type { FleetProjectSummary, SessionsListResult } from "../types.ts";
import {
  projectsForChatContext,
  resolveFleetProjectsContextSessionKey,
} from "../chat/fleet-projects-session.ts";
import {
  buildFleetSessionFilesTree,
  defaultFleetFilesExpandedDirs,
  findFirstFleetFilePath,
  type FleetFileReadTarget,
  type FleetFilesTreeNode,
} from "../chat/fleet-session-files-tree.ts";
import { resolveAgentIdForSession } from "../chat/resolve-session-agent.ts";
import { collectSpawnedSessionKeys } from "../chat/session-delegation-graph.ts";
import {
  formatChatWorkspaceError,
  readChatWorkspaceFile,
  type ChatWorkspaceState,
  type WorkspaceTreeNode,
} from "./chat-workspace.ts";
import { loadChatWorkspace } from "./chat-workspace.ts";

type WorkspaceListClient = {
  client: GatewayBrowserClient;
  agentId: string;
  sessionKey: string;
  projectId?: string | null;
};

async function fetchWorkspaceTree(params: WorkspaceListClient): Promise<WorkspaceTreeNode[]> {
  const req: Record<string, string> = {
    agentId: params.agentId,
    sessionKey: params.sessionKey,
  };
  if (params.projectId?.trim()) {
    req.projectId = params.projectId.trim();
  }
  try {
    const res = await params.client.request<{ tree?: WorkspaceTreeNode[] }>(
      "agents.workspace.list",
      req,
    );
    return Array.isArray(res?.tree) ? res.tree : [];
  } catch (err) {
    const msg = String(err);
    if (
      msg.includes("unexpected property 'sessionKey'") ||
      msg.includes('unexpected property "sessionKey"')
    ) {
      const legacy = await params.client.request<{ tree?: WorkspaceTreeNode[] }>(
        "agents.workspace.list",
        { agentId: params.agentId },
      );
      return Array.isArray(legacy?.tree) ? legacy.tree : [];
    }
    throw err;
  }
}

function resolveSubagentLabel(sessionKey: string, sessions: SessionsListResult | null): string {
  const row = sessions?.sessions?.find((s) => s.key?.trim() === sessionKey);
  const label = row?.label?.trim();
  if (label) {
    return label;
  }
  const parts = sessionKey.split(":");
  return parts.slice(-2).join(":") || sessionKey;
}

export type FleetSessionFilesState = ChatWorkspaceState & {
  fleetProjects: FleetProjectSummary[];
  sessionsResult: SessionsListResult | null;
  chatWorkspaceFileTargets: Record<string, FleetFileReadTarget>;
  chatWorkspaceExpandedDirs: Record<string, boolean>;
};

export async function loadFleetSessionFiles(state: FleetSessionFilesState) {
  if (!state.client || !state.connected) {
    state.chatWorkspaceTree = [];
    state.chatWorkspaceFileTargets = {};
    state.chatWorkspacePath = null;
    state.chatWorkspaceScope = null;
    state.chatWorkspaceError = null;
    return;
  }

  const defaultAgentId = state.agentsList?.defaultId ?? state.agentsDefaultId ?? "main";
  const contextKey = resolveFleetProjectsContextSessionKey(state.sessionKey, state.fleetProjects);
  const agentId = resolveAgentIdForSession(contextKey, defaultAgentId);
  const projects = projectsForChatContext(state.fleetProjects, state.sessionKey);
  const projectSlugs = new Set(projects.map((p) => p.slug));

  if (state.chatWorkspaceLoading && state.chatWorkspaceAgentId === agentId) {
    return;
  }

  state.chatWorkspaceLoading = true;
  state.chatWorkspaceError = null;
  state.chatWorkspaceAgentId = agentId;
  state.chatActiveProjectId = null;

  try {
    const sessionRes = await state.client.request<{
      workspace?: string;
      scope?: "sandbox" | "session";
      truncated?: boolean;
      tree?: WorkspaceTreeNode[];
    }>("agents.workspace.list", { agentId, sessionKey: contextKey });

    state.chatWorkspacePath = sessionRes?.workspace ?? null;
    state.chatWorkspaceScope = sessionRes?.scope ?? null;
    state.chatWorkspaceTruncated = Boolean(sessionRes?.truncated);
    const sessionTree = Array.isArray(sessionRes?.tree) ? sessionRes.tree : [];

    const projectTrees = await Promise.all(
      projects.map(async (project) => ({
        project,
        tree: await fetchWorkspaceTree({
          client: state.client!,
          agentId,
          sessionKey: contextKey,
          projectId: project.id,
        }),
      })),
    );

    const sessions = state.sessionsResult?.sessions ?? [];
    const spawnedKeys = collectSpawnedSessionKeys(contextKey, sessions).filter(
      (key) => key !== contextKey,
    );
    const subagentTrees = await Promise.all(
      spawnedKeys.map(async (sessionKey) => ({
        sessionKey,
        label: resolveSubagentLabel(sessionKey, state.sessionsResult),
        tree: await fetchWorkspaceTree({
          client: state.client!,
          agentId: resolveAgentIdForSession(sessionKey, defaultAgentId),
          sessionKey,
        }),
      })),
    );

    const workspaceLabel = state.chatWorkspacePath ? "Session workspace" : "Workspace files";
    const { tree, targets } = buildFleetSessionFilesTree({
      contextSessionKey: contextKey,
      workspaceLabel,
      sessionTree,
      projects: projectTrees,
      subagents: subagentTrees,
      projectSlugs,
    });

    state.chatWorkspaceTree = tree as unknown as WorkspaceTreeNode[];
    state.chatWorkspaceFileTargets = targets;
    state.chatWorkspaceExpandedDirs = defaultFleetFilesExpandedDirs(tree);

    const firstFile = findFirstFleetFilePath(tree);
    if (firstFile && state.chatWorkspaceSelectedPath !== firstFile) {
      await readFleetSessionFile(state, firstFile);
    } else if (!firstFile) {
      state.chatWorkspaceSelectedPath = null;
      state.chatWorkspaceFileContent = null;
      state.chatWorkspaceFileError = null;
    }
  } catch (err) {
    state.chatWorkspaceError = formatChatWorkspaceError(err);
    state.chatWorkspaceTree = [];
    state.chatWorkspaceFileTargets = {};
    state.chatWorkspacePath = null;
    state.chatWorkspaceScope = null;
  } finally {
    state.chatWorkspaceLoading = false;
  }
}

export async function readFleetSessionFile(state: FleetSessionFilesState, uiPath: string) {
  const target = state.chatWorkspaceFileTargets[uiPath];
  if (!target) {
    await readChatWorkspaceFile(state, uiPath);
    return;
  }
  if (!state.client || !state.connected) {
    return;
  }
  const defaultAgentId = state.agentsList?.defaultId ?? state.agentsDefaultId ?? "main";
  const agentId = resolveAgentIdForSession(target.sessionKey, defaultAgentId);
  const sessionKey = target.sessionKey;
  const path = target.path.trim();
  if (!path) {
    return;
  }

  state.chatWorkspaceSelectedPath = uiPath;
  state.chatWorkspaceFileLoading = true;
  state.chatWorkspaceFileError = null;

  const req: Record<string, string> = { agentId, sessionKey, path };
  if (target.kind === "project") {
    req.projectId = target.projectId;
  }

  try {
    const res = await state.client.request<{ content?: string; truncated?: boolean }>(
      "agents.workspace.read",
      req,
    );
    state.chatWorkspaceFileContent = res?.content ?? "";
    state.chatWorkspaceFileTruncated = Boolean(res?.truncated);
  } catch (err) {
    state.chatWorkspaceFileContent = null;
    state.chatWorkspaceFileError = formatChatWorkspaceError(err);
  } finally {
    state.chatWorkspaceFileLoading = false;
  }
}

/** Back-compat: non-composite workspace loads (e.g. inspector). */
export { loadChatWorkspace };
